// IE 2026 09 24 / ODOO-5800 (Igor: «Есть ли навести на точку графика в форме дроб дауна, то при зажатии Alt … ожидаю, что будет сразу показано расшифровку
// для ячейки»): ALT НАД ТОЧКОЙ ГРАФИКА ФОРМЫ ЯЧЕЙКИ — РАСШИФРОВКА ЯЧЕЙКИ ОТЧЁТА, КОТОРУЮ ЭТА ТОЧКА ИЗОБРАЖАЕТ.
//
// Что было: при зажатом Alt штатную подсказку графика глушит гейт App.vue (Tooltip.prototype.show: показом при Alt управляет utils/altTooltip), а у графика
// формы источника подсказки для Alt не было — над точкой с Alt не показывалось НИЧЕГО.
// Что стало: хост графика формы — источник altTooltip «по точке» (__altTipAt(x, y), altTooltip._find). Точка под курсором — свой подбор AttrChart.hoverPick
// (серия и индекс точки, тот же, что у подсветки серии), дальше точка → ячейки отчёта того же узла:
//   - узел — субъект строки ячейки формы (строка и предки, как измерения паспорта AttrGrid.cellPassport_load); сверка с названием узла серий паспорта
//     (series.node), при расхождении — поиск по названию в колонке subject;
//   - мера — серия графика → мера строк отчёта (поле measure строки или предков: open_balance / IN / OUT), признак непокрытого (alert Uncovered у строки или
//     предков) — отдельные серии *_uncovered; строки ищутся в блоке того же объекта (корень дерева), листья без memo-строк;
//   - период — дата точки → колонка '<YYYY-MM-01>.<мера колонки>': количество — мера колонки ячейки формы (quantity), DOS — колонка длительности того же
//     месяца (AttrTime / AttrDays: offset);
//   - приход / расход узла в отчёте разложен по группам первого уровня (Ex-Delta, Shipment from BW + In transit, Ex-Distr, Destruction …, часть групп скрыта
//     раскладкой) — берётся наименьший набор ВИДИМЫХ групп, чья сумма равна значению точки (затем — с учётом скрытых); точного набора нет — все видимые
//     ненулевые ячейки узла этой меры и итог;
//   - отметка самой ячейки (серия «This cell») — сама ячейка формы.
// Подсказка: сверху «Report cell» — по каждой ячейке строка «группа › измерения строки · месяц: значение, как в гриде» и под ней ТОТ ЖЕ текст, что грид
// показывает по Alt над этой ячейкой (AttrGrid.cellTooltip_html — единая точка подсказки ячейки, её же зовёт altTooltip над гридом и formatItem при Show
// tooltips); ниже, через черту, — обычная подсказка графика (AttrChart.tooltipContent: дата и значения серий). Нет ячейки (зоны запасов, DOS страны, линия
// Stock uncovered без строки в отчёте) — только обычная подсказка графика. Рядом с точкой (справа, с переносом у края), сразу; отпустил Alt / ушёл с точки —
// подсказка скрывается (без «дожития» Alt-подсказок, altTooltip: noLinger).
// Выключатель: fe.cellForm.chartAltExplain (store.setting — тот же реестр feSettings, что остальные fe.cellForm.*; по умолчанию ВКЛ):
// window.feSettings.set('fe.cellForm.chartAltExplain', false) — как было (над точкой с Alt ничего). Диагностика: window.cellChartAlt.last / .stats.
// IE 2026 09 25 / ODOO-5800 (договорённость с ie-webscm7, письмо #43150): УСТУПКА ui-help — на том же хосте графика стоит карточка «Chart area» (v-uiHelp
// 'chart.line.plot', utils/uiHelp/charts.js), и над точкой она перекрывала расшифровку ячейки. Протокол: хост графика формы несёт data-alt-owner="cellChartAlt"
// (Dropdown.vue, только при включённом флаге), а модуль отдаёт window.cellChartAlt.isActive() — true, когда Alt зажат и под курсором точка / столбик, для которых
// нашлась ячейка отчёта (над зоной, линией без строки в отчёте и пустой областью — false: там карточка коллеги работает как у всех графиков). Координаты —
// аргументы (x, y) либо последнее положение мыши контроллера altTooltip; хост — последняя привязка cellChartAlt_bind (форма ячейки одна на приложение).
import { Tooltip, PopupPosition, Rect } from '@mescius/wijmo'
import { t, tn } from '@/i18n'
import altTooltip from './altTooltip'   // IE 2026 09 25 / ODOO-5800: состояние Alt и последнее положение мыши для isActive()

const FLAG = 'fe.cellForm.chartAltExplain'
export const CHART_ALT_OWNER = 'cellChartAlt'   // IE 2026 09 25 / ODOO-5800: значение data-alt-owner хоста графика формы = имя объекта window.<owner> с isActive()
const MAX_CELLS = 6       // строк ячеек в подсказке; остальные — «+N more»
const SUM_GROUPS = 8      // групп первого уровня в переборе наборов (2^8)

// серия графика формы (utils/cellChart.js SERIES; производные поля отрисовки — к исходному ключу) → ячейки отчёта:
// m — мера строк (measure строки / предков), unc — строки непокрытого, dur — колонка длительности месяца (DOS), sum — подбор групп по сумме, own — ячейка формы
const SERIES_CELL = {
    open_balance: { m: 'open_balance' },
    open_balance_uncovered: { m: 'open_balance', unc: true },
    DOS: { m: 'open_balance', dur: true },
    IN: { m: 'IN', sum: true },
    IN_uncovered: { m: 'IN', unc: true, sum: true },
    OUT: { m: 'OUT', sum: true },
    OUT_uncovered: { m: 'OUT', unc: true, sum: true },
    WO_OUT: { m: 'OUT', sum: true },   // списание (Destruction) — строки расхода своей группы; группа находится подбором по сумме
    cell_mark: { own: true },
}
const PLOT_SUFFIX = /_(line|plot)$/   // производные поля отрисовки серий (open_balance_uncovered_line) → исходный ключ

const stats = { calls: 0, points: 0, cells: 0, chartOnly: 0, none: 0, ms: 0, isActive: 0, active: 0 }   // IE 2026 09 25 / ODOO-5800: isActive / active — запросы владельца и ответы true
// IE 2026 09 25 / ODOO-5800: owner / isActive — контракт уступки для ui-help (window[data-alt-owner].isActive()); объявление функции поднято, ссылка законна
const debug = { stats, last: null, owner: CHART_ALT_OWNER, isActive: (x, y) => chartAlt_isActive(x, y) }
if (typeof window !== 'undefined') window.cellChartAlt = debug

export function chartAlt_on(store) {
    try { return !(store && typeof store.setting === 'function' && store.setting(FLAG, true) === false) } catch (e) { return true }
}

let _tip = null
// свой инстанс подсказки: справа от точки, узкий зазор (у общего altTooltip.tip App.vue ставит gap 50 — там точка терялась бы); класс — как у штатных подсказок
function tipInst() {
    if (!_tip) {
        _tip = new Tooltip({ position: PopupPosition.RightTop, gap: 12, cssClass: 'wj-tooltip-my' })
        _tip.showDelay = 1   // App.vue ставит gap 50 инстансам без showDelay (подсказки кнопок) — этому нет
    }
    return _tip
}

const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
const strip = (h) => String(h ?? '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
const num = (v) => { const n = Number(v); return (v === null || v === undefined || v === '' || !Number.isFinite(n)) ? null : n }

// значение поля строки грида: своё поле либо строка _DLRow.where строки (у строк отчёта измерения лежат и там, и там)
function dimOf(it, k) {
    if (!it) return null
    const own = it[k]
    if (own !== undefined && own !== null && own !== '' && typeof own !== 'object') return String(own)
    for (const w of (it._DLRow && it._DLRow.where) || []) {
        const kk = String((w && w.leftValue && w.leftValue.value) ?? (w && w.leftValue) ?? '')
        if (kk !== k) continue
        const v = (w && w.rightValue && w.rightValue.value) ?? (w && w.rightValue)
        if (v !== undefined && v !== null && v !== '' && typeof v !== 'object') return String(v)
    }
    return null
}

// подписи строки: первые непустые нечисловые значения колонок-измерений (как якорь формы AttrGrid.cellForm_anchorText)
function dimsText(flex, r) {
    const parts = []
    for (let c = 0; c < flex.columns.length && parts.length < 3; c++) {
        const col = flex.columns[c]
        if (!col.visible || /^\d{4}-\d{2}-\d{2}/.test(col.binding || '')) continue
        const v = String(flex.getCellData(r, c, true) ?? '').trim()
        if (v && !/^[\d\s.,%−-]+$/.test(v)) parts.push(v.length > 32 ? v.slice(0, 31) + '…' : v)
    }
    return parts.join(' / ')
}

// memo-строка («· of which …» — часть строки-родителя, AttrGrid.memoRow_is): не ячейка узла и не делает родителя группой
const isMemo = (it) => !!(it && it._DLRow && it._DLRow.memo)

// предки строки (строка и вверх по уровням) и подпись: группа первого уровня блока объекта › измерения строки; p — непосредственный родитель
function chainOf(rows, r) { const out = [r]; let lvl = rows[r].level; for (let i = r - 1; i >= 0 && lvl > 0; i--) { if (rows[i].level < lvl) { lvl = rows[i].level; out.push(i) } } return out }
function rowLabel(flex, r) {
    const ch = chainOf(flex.rows, r)
    const top = ch[ch.length - 1], lvl1 = flex.rows[top].level + 1
    const g = ch.find(i => flex.rows[i].level === lvl1)
    return { group: g == null || g === r ? '' : dimsText(flex, g), dims: dimsText(flex, r), p: ch.length > 1 ? ch[1] : null, g: g == null ? null : g }
}

// индекс ячеек узла в блоке объекта ячейки формы: листья с мерой, признаком непокрытого, группой первого уровня; кэш — на ячейку формы и состав строк
let _ix = null
function nodeIndex(dd, grid) {
    const flex = grid.flex, rows = flex.rows
    const a = dd.anchor_cell
    const series = dd.cell_series || {}
    const key = [dd.key, rows.length, series.node || '', series.center || '', a && a.row].join('|')
    if (_ix && _ix.key === key && _ix.flex === flex) return _ix
    _ix = null
    if (!a || a.row < 0 || a.row >= rows.length) return null
    const chain0 = chainOf(rows, a.row)   // строка формы и её предки
    // блок объекта: от корня (уровень 0) до следующего корня
    const top = chain0[chain0.length - 1]
    let end = rows.length; for (let i = top + 1; i < rows.length; i++) if (rows[i].level <= rows[top].level) { end = i; break }
    // узел: субъект строки формы или ближайшего предка (как измерения паспорта); сверка с названием узла серий
    let subj = null
    for (const r of chain0) { subj = dimOf(rows[r].dataItem, 'subject'); if (subj) break }
    const cSubj = flex.columns.findIndex(c => c.binding === 'subject')
    const subjTitle = (id) => { if (cSubj < 0) return ''; for (let r = top; r < end; r++) if (rows[r].dataItem && String(rows[r].dataItem.subject || '') === id) return String(flex.getCellData(r, cSubj, true) ?? '').trim(); return '' }
    const node = String(series.node || '').trim()
    if (node && cSubj >= 0 && (!subj || subjTitle(subj) !== node)) {
        for (let r = top; r < end; r++) {
            const it = rows[r].dataItem
            if (it && it.subject && String(flex.getCellData(r, cSubj, true) ?? '').trim() === node) { subj = String(it.subject); break }
        }
    }
    if (!subj) return null
    // листья узла: мера / непокрытое — у строки или предков; группа — предок первого уровня (видимость — явная, свёрнутость не в счёт)
    const cells = []
    for (let r = top + 1; r < end; r++) {
        const row = rows[r], it = row.dataItem
        if (!it || String(it.subject || '') !== subj || isMemo(it)) continue
        let leaf = true   // лист: подчинённых нет или все они memo-строки («· of which expired» под строкой остатка склада)
        for (let i = r + 1; i < end && rows[i].level > row.level; i++) if (!isMemo(rows[i].dataItem)) { leaf = false; break }
        if (!leaf) continue
        const ch = chainOf(rows, r)
        let m = null, unc = false
        for (const i of ch) {
            const x = rows[i].dataItem
            if (!m) m = dimOf(x, 'measure')
            if (/^uncovered$/i.test(String(dimOf(x, 'alert') || '').trim())) unc = true
        }
        if (m && /_uncovered$/i.test(m)) { unc = true; m = m.replace(/_uncovered$/i, '') }
        const L = rowLabel(flex, r)
        cells.push({ r, m, unc, g: L.g, p: L.p, gVisible: L.g == null ? true : rows[L.g].visible !== false, gTitle: L.group, dims: L.dims })
    }
    _ix = { key, flex, subj, node, top, end, cells }
    return _ix
}

// колонки месяца: количество — мера колонки ячейки формы (если это не длительность), DOS — колонка длительности того же месяца
function monthColumns(grid, date, formBinding) {
    const flex = grid.flex, A = grid.attr_by_binding || {}
    const isDur = (b) => { const x = A[b]; return !!x && (x.attr_type === 'AttrTime' || x.attr_type === 'AttrDays') }
    const pre = date + '.'
    const own = String(formBinding || '').replace(/^\d{4}-\d{2}-\d{2}\./, '')
    let qty = -1, dur = -1, qn = -1, first = -1
    flex.columns.forEach((c, i) => {
        const b = String(c.binding || '')
        if (!b.startsWith(pre)) return
        if (isDur(b)) { if (dur < 0) dur = i; return }
        if (b === pre + own) qty = i            // мера колонки ячейки формы
        if (b === pre + 'quantity') qn = i
        if (first < 0) first = i
    })
    if (qty < 0) qty = qn >= 0 ? qn : first     // форма открыта на колонке длительности — количество месяца
    return { qty, dur }
}

// текст ячейки, как её рисует грид: длительности — тем же форматтером (formatItem → formatTimeHTML / formatDaysHTML), остальное — форматом колонки
function cellText(grid, r, c) {
    const flex = grid.flex, b = flex.columns[c].binding, a = (grid.attr_by_binding || {})[b] || {}
    const raw = flex.getCellData(r, c, false)
    if (raw === null || raw === undefined || raw === '') return ''
    try {
        if (a.attr_type === 'AttrTime') return strip(grid.store.formatTimeHTML(raw, { attr_type: 'AttrTime', format: a.format, unit: a.unit }))
        if (a.attr_type === 'AttrDays') return strip(grid.store.formatDaysHTML(raw, { attr_type: 'AttrDays', format: a.format }))
    } catch (e) { /* форматтер недоступен — как число */ }
    return String(flex.getCellData(r, c, true) ?? '').trim()
}

// наименьший набор групп, сумма которых равна значению точки: сначала только видимые группы, затем все; null — точного набора нет
function pickGroups(groups, V) {
    const tol = Math.max(0.5, Math.abs(V) * 1e-6)
    const tryIn = (list) => {
        const n = Math.min(list.length, SUM_GROUPS)
        let best = null
        for (let mask = 1; mask < (1 << n); mask++) {
            let s = 0, k = 0
            for (let i = 0; i < n; i++) if (mask & (1 << i)) { s += list[i].sum; k++ }
            if (Math.abs(s - V) <= tol && (!best || k < best.k)) best = { mask, k }
        }
        return best ? list.filter((x, i) => i < n && (best.mask & (1 << i))) : null
    }
    return tryIn(groups.filter(g => g.visible && g.sum !== 0)) || tryIn(groups.filter(g => g.sum !== 0))
}

// ячейки отчёта для точки: [{r, c, binding, group, dims, month, text, value}]
function cellsOf(dd, grid, key, item) {
    const spec = SERIES_CELL[key]
    if (!spec) return []
    const flex = grid.flex
    const date = String(item && item.date || '').slice(0, 10)
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return []
    const month = date.slice(0, 7)
    const a = dd.anchor_cell
    const formBinding = a && flex.columns[a.col] ? flex.columns[a.col].binding : ''
    if (spec.own) {
        if (!a || !String(formBinding).startsWith(date + '.')) return []
        const L = rowLabel(flex, a.row)
        return [{ r: a.row, c: a.col, group: L.group, dims: L.dims, p: L.p, month }]
    }
    const ix = nodeIndex(dd, grid)
    if (!ix) return []
    const cols = monthColumns(grid, date, formBinding)
    const c = spec.dur ? cols.dur : cols.qty
    if (c < 0) return []
    const b = flex.columns[c].binding
    const val = (r) => num(grid.store.get_value(flex.rows[r].dataItem, b))
    const cand = ix.cells.filter(x => x.m === spec.m && x.unc === !!spec.unc)
    if (!cand.length) return []
    let pick
    if (spec.sum) {
        const V = num(item[key])
        const byG = new Map()
        for (const x of cand) {
            const v = val(x.r)
            if (!v) continue
            const g = byG.get(x.g) || { g: x.g, visible: x.gVisible, sum: 0, cells: [] }
            g.sum += v; g.cells.push(x); byG.set(x.g, g)
        }
        const groups = [...byG.values()]
        const hit = V != null ? pickGroups(groups, V) : null
        pick = (hit || groups.filter(g => g.visible)).flatMap(g => g.cells)
    } else {
        // остаток / DOS: строка остатка узла; несколько — видимые группы, при нескольких — та, чьё количество равно точке запаса
        pick = cand.filter(x => x.gVisible)
        if (!pick.length) pick = cand
        if (pick.length > 1 && cols.qty >= 0) {
            const S = num(item.open_balance), bq = flex.columns[cols.qty].binding
            const same = S == null ? [] : pick.filter(x => num(grid.store.get_value(flex.rows[x.r].dataItem, bq)) === S)
            if (same.length) pick = same
        }
    }
    return pick.map(x => ({ r: x.r, c, group: x.gTitle, dims: x.dims, p: x.p, month }))
}

// IE 2026 09 25 / ODOO-5800: график формы, готовый к подбору точки (флаг включён, компонент с отрисовкой), — общий шаг подсказки и isActive()
function chartOf(dd) {
    if (!dd || !chartAlt_on(dd.store)) return null
    const vm = typeof dd.cellChart_vue === 'function' ? dd.cellChart_vue() : null
    const flex = vm && vm.flex
    if (!flex || !flex.hostElement || typeof vm.hoverPick !== 'function') return null
    return { vm, flex }
}

// IE 2026 09 25 / ODOO-5800: точка под курсором (серия и элемент данных, как у подсветки серии) и ячейки отчёта, которые она изображает; null — точки нет
function pickAt(dd, ch, x, y) {
    const ht = ch.vm.hoverPick(ch.flex, { clientX: x, clientY: y })
    if (!ht || !ht.series || !ht.item) return null
    const binding = String(ht.series.binding || '')
    const key = binding.replace(PLOT_SUFFIX, '')
    const grid = dd.vueObj
    let cells = []
    if (grid && grid.flex && typeof grid.cellTooltip_html === 'function') {
        try { cells = cellsOf(dd, grid, key, ht.item) } catch (e) { cells = [] }
    }
    return { ht, binding, key, grid, cells }
}

// содержимое подсказки для точки (или null — точки под курсором нет)
function tipFor(dd, x, y) {
    const t0 = performance.now()
    stats.calls++
    try {
        const ch = chartOf(dd)   // IE 2026 09 25 / ODOO-5800: общий шаг с isActive()
        if (!ch) return null
        const { vm, flex } = ch
        const p = pickAt(dd, ch, x, y)
        if (!p) { stats.none++; return null }
        stats.points++
        const { ht, binding, key, grid, cells } = p
        // точка: координаты в окне (справа от неё — подсказка)
        let rect = null
        try {
            const hr = flex.hostElement.getBoundingClientRect()
            const px = flex.axisX.convert(vm.hoverXNum(flex.bindingX ? ht.item[flex.bindingX] : ht.pointIndex, ht.pointIndex))
            const py = (ht.series.axisY || flex.axisY).convert(+ht.item[binding])
            if (Number.isFinite(px) && Number.isFinite(py)) rect = new Rect(hr.left + px - 4, hr.top + py - 4, 8, 8)
        } catch (e) { rect = null }
        // обычная подсказка графика (дата и значения серий) — тот же построитель, что без Alt
        let chartHtml = ''
        try { chartHtml = vm.tooltipContent({ item: ht.item, pointIndex: ht.pointIndex, series: ht.series, chart: flex }) || '' } catch (e) { chartHtml = '' }
        const out = []
        const seen = new Map()
        for (const c of cells) { const k = c.group + '|' + c.dims; seen.set(k, (seen.get(k) || 0) + 1) }
        for (const c of cells) {
            const text = cellText(grid, c.r, c.c)
            let tip = ''
            try { tip = grid.cellTooltip_html(grid.flex.cells, c.r, c.c) || '' } catch (e) { tip = '' }
            // одинаковые подписи в группе (строки узла, разбитые по стране продаж и т. п.) — с подписью родителя
            const dup = seen.get(c.group + '|' + c.dims) > 1 && c.p != null
            const dims = dup ? dimsText(grid.flex, c.p) + ' › ' + c.dims : c.dims
            out.push({ ...c, text, tip, dims, binding: grid.flex.columns[c.c].binding, value: num(grid.flex.getCellData(c.r, c.c, false)) })
        }
        let html = ''
        if (out.length) {
            html += `<div class="tooltip-header" style="margin-bottom:3px">${esc(out.length === 1 ? t('Report cell') : t('Report cells: {n}', { n: out.length }))}</div>`
            for (const c of out.slice(0, MAX_CELLS)) {
                // подпись переносится по пути строки, месяц и значение — неразрывно (узкая подсказка не рвёт «2026-08» и «1.5m»)
                const label = (c.group ? `<b>${esc(c.group)}</b> › ` : '') + esc(c.dims) + ` · <span style="white-space:nowrap">${esc(c.month)}</span>`
                html += `<div style="display:flex;justify-content:space-between;align-items:baseline;gap:14px"><span>${label}</span><b class="tooltip-text" style="white-space:nowrap">${esc(c.text || '—')}</b></div>`
                if (c.tip) html += `<div style="margin:2px 0 4px 12px">${c.tip}</div>`
            }
            if (out.length > MAX_CELLS) html += `<div class="tooltip-text">${esc(tn(out.length - MAX_CELLS, '+{n} more', '+{n} more'))}</div>`
            const qty = out.filter(c => c.value != null && !(grid.attr_by_binding || {})[c.binding]?.attr_type?.match(/^Attr(Time|Days)$/))
            if (qty.length > 1) {
                const s = qty.reduce((acc, c) => acc + c.value, 0)
                html += `<div style="display:flex;justify-content:space-between;gap:14px"><span>${esc(t('Total'))}</span><b class="tooltip-text" style="white-space:nowrap">${esc(grid.store.formatCount(Math.round(s * 1000) / 1000))}</b></div>`
            }
            if (chartHtml) html += '<div style="border-top:1px solid rgba(0,0,0,.15);margin:6px 0 5px"></div>' + chartHtml
            stats.cells++
        } else {
            html = chartHtml
            stats.chartOnly++
        }
        debug.last = { series: key, month: String(ht.item.date || '').slice(0, 7), point: ht.pointIndex, cells: out.map(c => ({ r: c.r, c: c.c, binding: c.binding, group: c.group, dims: c.dims, text: c.text, tip: c.tip })), html }
        if (!html) return null
        return { content: `<div class="cell-chart-alt">${html}</div>`, rect, inst: tipInst(), noLinger: true }
    } catch (e) {
        if (!(dd && dd.store && dd.store.hide_console)) console.warn('cellChartAlt: tooltip failed', e)
        return null
    } finally {
        stats.ms += performance.now() - t0
    }
}

// хост графика формы — источник Alt-подсказки «по точке» (altTooltip._find: __altTipAt, data-alt-tip-at снимает дедуп по элементу под мышью: один элемент SVG —
// много точек); вызывается из Dropdown.cellChart_hook на каждое обновление графика; флаг выключен — источник ничего не отдаёт (как было)
let _bound = null   // IE 2026 09 25 / ODOO-5800: последняя привязка { host, dd } — хост графика формы для isActive() (форма ячейки одна, хост меняется при пересоздании чарта)
export function cellChartAlt_bind(dd, flex) {
    const host = flex && flex.hostElement
    if (!host) return
    host.__altTipAt = (x, y) => tipFor(dd, x, y)   // флаг проверяется при показе — переключение без перерисовки графика
    host.setAttribute('data-alt-tip-at', '')
    _bound = { host, dd }   // IE 2026 09 25 / ODOO-5800
}

// IE 2026 09 25 / ODOO-5800 (контракт с ui-help, письмо #43150): наш механизм готов показать подсказку по точке под курсором — Alt зажат И под курсором
// точка / столбик графика формы, для которых нашлась ячейка отчёта; зона, линия без строки в отчёте, пустая область, отпущенный Alt, закрытая форма,
// выключенный флаг — false (карточка «Chart area» коллеги там показывается как у всех графиков). (x, y) — координаты в окне; без них — положение мыши altTooltip
export function chartAlt_isActive(x, y) {
    stats.isActive++
    try {
        if (!altTooltip.held) return false
        const px = (x === undefined || x === null) ? altTooltip._x : +x
        const py = (y === undefined || y === null) ? altTooltip._y : +y
        if (!(px >= 0) || !(py >= 0)) return false
        const b = _bound
        if (!b || !b.host.isConnected) return false
        const r = b.host.getBoundingClientRect()   // курсор вне хоста графика — точки быть не может (hoverPick считает от хоста)
        if (px < r.left || px > r.right || py < r.top || py > r.bottom) return false
        const ch = chartOf(b.dd)
        if (!ch || ch.flex.hostElement !== b.host) return false
        const p = pickAt(b.dd, ch, px, py)
        const on = !!(p && p.cells.length)
        if (on) stats.active++
        return on
    } catch (e) {
        return false
    }
}

export default { chartAlt_on, chartAlt_isActive, cellChartAlt_bind, CHART_ALT_OWNER }
